import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { humanDateWithoutTime, humanEndDate } from "sj-ui-toolkit/src/utils/dates";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { skeletonRows, withSkeleton } from "@/helpers/tableBody";
import PlaArchiveDialog from "./PlaArchiveDialog";
import type { ApprovedPlaAssignmentRow } from "./types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_FILTERS = {
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  displayName: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  email: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
};

const CONFIGURABLE_COLUMNS = [
  {
    field: "attributes.employeeNumber",
    header: "Employee #",
    sortable: false,
    visibleInitially: false,
  },
  {
    field: "attributes.jobRole",
    header: "Job Role",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.title",
    header: "Title",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.country",
    header: "Country",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.company",
    header: "Company",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.businessUnit",
    header: "Business Unit",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.teamName",
    header: "Team Name",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.managersEmail",
    header: "Manager's Email",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.managersName",
    header: "Manager",
    sortable: true,
    visibleInitially: false,
  },
  {
    field: "attributes.department",
    header: "Department",
    sortable: true,
    visibleInitially: false,
  },
];

const GLOBAL_FILTER_FIELDS = ["displayName", "email", "givenName", "familyName"];

// ---------------------------------------------------------------------------
// Column body templates
// ---------------------------------------------------------------------------

const learnerBody = withSkeleton<ApprovedPlaAssignmentRow>(row => (
  <div>
    <div className="font-semibold">{row.displayName}</div>
    <div className={`body-sm ${themeClasses.textSecondary}`}>{row.email}</div>
  </div>
));

const approvedAtBody = withSkeleton<ApprovedPlaAssignmentRow>(row =>
  row.approvedAt ? humanDateWithoutTime(row.approvedAt) : "--"
);

const dueDateBody = withSkeleton<ApprovedPlaAssignmentRow>(row =>
  row.endDate ? humanEndDate(row.endDate) : "--"
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PersonalizedLessonAssignmentsTableProps {
  assignments: ApprovedPlaAssignmentRow[];
  isLoading: boolean;
  onRefetch?: () => Promise<void> | void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PersonalizedLessonAssignmentsTable = ({
  assignments,
  isLoading,
  onRefetch,
}: PersonalizedLessonAssignmentsTableProps) => {
  const dt = useRef<DataTable<DataTableValueArray>>(null);
  const navigate = useNavigate();
  const stateKey = `pla-assignments-table-${getCurrentSelectedTenant() ?? ""}`;
  const [archiveTarget, setArchiveTarget] = useState<ApprovedPlaAssignmentRow | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [visibleColumns, setVisibleColumns] = useState(
    CONFIGURABLE_COLUMNS.filter(c => c.visibleInitially)
  );

  const storedRows = useMemo(() => {
    try {
      const stored = localStorage.getItem(stateKey);
      if (stored) {
        const parsed: { rows?: number } = JSON.parse(stored);
        if (parsed.rows) return parsed.rows;
      }
    } catch {
      // ignore
    }
    return DEFAULT_ROW_COUNT;
  }, []);

  const skeletonItems = useMemo(
    () => skeletonRows<ApprovedPlaAssignmentRow>(storedRows),
    [storedRows]
  );

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, global: { ...prev.global, value } }));
    setGlobalFilterValue(value);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setGlobalFilterValue("");
  };

  const onColumnSelectChange = (e: { value: string[] }) => {
    setVisibleColumns(CONFIGURABLE_COLUMNS.filter(c => e.value.includes(c.field)));
  };

  const editRoute =
    AdminAppRoutes.routes.assignments.routes.personalizedLessonAssignment.routes.edit.route;

  const lessonsBody = withSkeleton<ApprovedPlaAssignmentRow>(
    row => `${row.lessonsTotal} ${row.lessonsTotal === 1 ? "lesson" : "lessons"}`,
    "8rem"
  );

  const actionsBody = withSkeleton<ApprovedPlaAssignmentRow>(row => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        text
        rounded
        severity="secondary"
        aria-label={`Edit assignment for ${row.displayName}`}
        onClick={() =>
          navigate(editRoute.replace(":uuid", row.uuid), {
            state: { learnerName: row.displayName },
          })
        }
      />
      <Button
        icon="pi pi-trash"
        text
        rounded
        severity="secondary"
        aria-label={`Archive assignment for ${row.displayName}`}
        onClick={() => setArchiveTarget(row)}
      />
    </div>
  ));

  const renderHeader = () => (
    <div className="flex flex-wrap justify-between gap-4 mb-4">
      <div className="flex items-center gap-4">
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search"
            aria-label="Search assignments"
            autoComplete="off"
          />
        </IconField>
        <Button
          label="Clear"
          icon="pi pi-filter-slash"
          size="small"
          severity="secondary"
          className={globalFilterValue ? "" : "hidden"}
          onClick={clearFilters}
        />
      </div>
      <div className="flex items-center gap-4">
        <MultiSelect
          value={visibleColumns.map(c => c.field)}
          onChange={onColumnSelectChange}
          options={CONFIGURABLE_COLUMNS}
          optionLabel="header"
          optionValue="field"
          placeholder="Select Columns"
          aria-label="Select visible columns"
          className="w-48"
          maxSelectedLabels={2}
        />
        <Button
          label="Export data"
          icon="pi pi-download"
          iconPos="left"
          size="small"
          disabled={isLoading}
          onClick={() => dt.current?.exportCSV({ selectionOnly: false })}
          tooltip="Export data as CSV"
          tooltipOptions={{ position: "left" }}
        />
      </div>
    </div>
  );

  return (
    <>
      <Card className={themeClasses.bgElevation1}>
        {renderHeader()}
        <DataTable
          ref={dt}
          aria-label="Approved personalized lesson assignments list"
          data-testid="pla-assignments-table"
          value={(isLoading ? skeletonItems : assignments) as unknown as DataTableValueArray}
          stateKey={stateKey}
          stateStorage="local"
          paginator
          filters={isLoading ? undefined : filters}
          onFilter={e => setFilters({ ...EMPTY_FILTERS, ...(e.filters as typeof EMPTY_FILTERS) })}
          filterDisplay="menu"
          size="normal"
          globalFilterFields={isLoading ? [] : GLOBAL_FILTER_FIELDS}
          rows={storedRows}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          removableSort
          emptyMessage="No approved personalized lesson assignments"
          tableClassName="dt-responsive-text"
          onStateSave={(state: Record<string, unknown>) => {
            const cleaned = { ...state };
            delete cleaned.sortField;
            delete cleaned.sortOrder;
            localStorage.setItem(stateKey, JSON.stringify(cleaned));
          }}
        >
          <Column
            field="displayName"
            header="Learner"
            sortable
            filter
            filterPlaceholder="Search by name or email"
            body={learnerBody}
            style={{ minWidth: "12rem" }}
          />
          <Column
            field="lessonsTotal"
            header="Lessons"
            sortable
            body={lessonsBody}
            style={{ minWidth: "6rem" }}
          />
          <Column
            field="approvedAt"
            header="Approved"
            sortable
            body={approvedAtBody}
            style={{ minWidth: "8rem" }}
          />
          <Column
            field="endDate"
            header="Due Date"
            sortable
            body={dueDateBody}
            style={{ minWidth: "8rem" }}
          />
          <Column field="actions" header="Actions" body={actionsBody} />
          {CONFIGURABLE_COLUMNS.filter(c => visibleColumns.some(v => v.field === c.field)).map(
            col => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={col.sortable}
                style={{ minWidth: "8rem" }}
              />
            )
          )}
        </DataTable>
      </Card>
      <PlaArchiveDialog
        isVisible={archiveTarget !== null}
        assignmentUuid={archiveTarget?.assignmentUuid}
        learnerName={archiveTarget?.displayName}
        onComplete={success => {
          setArchiveTarget(null);
          if (success) {
            void onRefetch?.();
          }
        }}
      />
    </>
  );
};

export default PersonalizedLessonAssignmentsTable;
